/**
 * Every query key the app uses, from one factory, so a key is never spelt twice
 * and invalidation can name a whole family by its first segment.
 *
 * A key is `[family, ...narrowing]`. The family is what the live feed invalidates
 * (`lib/query/invalidation.ts` maps written tables to families); the rest only
 * tells two entries of one family apart. Filters go last, as an object, so that
 * `["queue", "me"]` still matches every filter of the queue for `me`.
 */
export const FAMILIES = [
  "boards",
  "users",
  "projects",
  "project-usage",
  "agents",
  "agent-messages",
  "agent-turns",
  "queue",
  "awaiting",
  "status",
  "apps",
  "claude-accounts",
  "activity",
  "task",
  "response",
  "intake",
  "whatsapp",
  "mcp-tokens",
  "notifications",
  "voice",
  "browse",
  "layouts",
  "mcp-servers",
  "canvas",
  "previews",
  "remote-machines",
  "engine-settings",
] as const;

export type QueryFamily = (typeof FAMILIES)[number];

export interface ActivityFilters {
  /** The boards whose trails are merged; order-insensitive. */
  boardIds: string[];
  limit?: number;
}

export const qk = {
  /** Every board with its columns, cards and counts — archived included, split client-side. */
  boards: () => ["boards"] as const,
  users: () => ["users"] as const,
  projects: () => ["projects"] as const,
  /** What points at each project — counts of boards, cards, agents. Its own family: a card move changes it, not the list. */
  projectOverviews: () => ["project-usage", "overview"] as const,
  projectUsage: (projectId: string) => ["project-usage", projectId] as const,
  /** A project's env files (key names only). Under `projects`: an edit to the project refreshes it too. */
  projectEnv: (projectId: string) => ["projects", "env", projectId] as const,
  /** A folder listing for the project picker; `""` is the list of roots. */
  projectBrowse: (path: string | null | undefined) => ["browse", path ?? ""] as const,

  agents: () => ["agents"] as const,
  agentTriggers: (agentId: string) => ["agents", agentId, "triggers"] as const,
  /** The Agents list's folders. Under `agents`, so refreshing the list refreshes them with it. */
  agentFolders: () => ["agents", "folders"] as const,
  /** An agent's transcript, as an infinite query of pages going back in time. Kept current by the websocket. */
  agentMessages: (agentId: string) => ["agent-messages", agentId] as const,
  agentTurns: (agentId: string, status: string) => ["agent-turns", agentId, status] as const,

  /** A person's cards across boards. */
  queue: (assignee: string, filters: { includeDone: boolean }) => ["queue", assignee, filters] as const,
  /** Everything waiting on the user's answer (admin only). */
  awaiting: () => ["awaiting"] as const,
  status: (tz: string) => ["status", tz] as const,
  apps: () => ["apps"] as const,
  claudeAccounts: () => ["claude-accounts"] as const,
  engineSettings: () => ["engine-settings"] as const,
  activity: (filters: ActivityFilters) =>
    ["activity", { boardIds: [...filters.boardIds].sort(), limit: filters.limit ?? 40 }] as const,

  /** A canvas board's whole graph: nodes with their cards' live state, and arrows. */
  canvas: (boardId: string) => ["canvas", boardId] as const,

  /** One card's thread and what hangs off it. The card itself is in `boards`. */
  task: (taskId: string) => ["task", taskId] as const,
  taskComments: (taskId: string) => ["task", taskId, "comments"] as const,
  taskMentions: (taskId: string) => ["task", taskId, "mentions"] as const,
  taskPhotos: (taskId: string) => ["task", taskId, "photos"] as const,
  /** The canvas boards a card is a node on (read from its detail). */
  taskCanvases: (taskId: string) => ["task", taskId, "canvases"] as const,
  taskResponses: (taskId: string) => ["task", taskId, "responses"] as const,
  response: (responseId: string) => ["response", responseId] as const,

  intake: (boardId: string) => ["intake", boardId] as const,
  whatsapp: (boardId: string) => ["whatsapp", boardId] as const,
  mcpTokens: (boardId: string | null) => ["mcp-tokens", boardId ?? "*"] as const,
  voiceEngines: () => ["voice"] as const,
  /** The signed-in person's push settings: which kinds are on, and their subscribed devices. */
  notificationPrefs: () => ["notifications", "prefs"] as const,
  /** Whether the server can send push at all (its VAPID public key). */
  pushConfig: () => ["notifications", "config"] as const,
  pushDevices: () => ["notifications", "devices"] as const,
  /** Saved window layouts (the Windows menu). The live workspace itself is per device, in localStorage. */
  layouts: () => ["layouts"] as const,
  /** Every open dev-server preview (admins); the chat header and list rows filter by agent. */
  previews: () => ["previews"] as const,
  /** The registry of MCP servers (admins; an agent carries its own list). */
  mcpServers: () => ["mcp-servers"] as const,
  /** Remote machines (admins) with the host worker's sync state. Never a key. */
  remoteMachines: () => ["remote-machines"] as const,
} as const;

/** The family a key belongs to, or null for a key this factory did not make. */
export function familyOf(queryKey: readonly unknown[]): QueryFamily | null {
  const head = queryKey[0];
  return typeof head === "string" && (FAMILIES as readonly string[]).includes(head) ? (head as QueryFamily) : null;
}
